import { and, eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo } from 'react'
import { useAuth } from '@/lib/auth'
import { useStore } from '@/lib/pocketbase'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

export function useActiveEntry() {
    const { user } = useAuth()
    const [timeEntriesCollection, jobsCollection, locationsCollection] = useStore(
        'time_entries',
        'jobs',
        'locations'
    )

    const { isLoading, data: rawEntries = [] } = useLiveQuery(
        q =>
            user?.id
                ? q
                      .from({ entries: timeEntriesCollection })
                      .join(
                          { job: jobsCollection },
                          ({ entries, job }) => eq(entries.job, job.id),
                          'left'
                      )
                      .join(
                          { location: locationsCollection },
                          ({ entries, location }) => eq(entries.location, location.id),
                          'left'
                      )
                      .where(({ entries }) =>
                          and(eq(entries.user, user.id), eq(entries.status, 'active'))
                      )
                      .orderBy(({ entries }) => entries.clock_in, 'desc')
                : undefined,
        [user?.id]
    )

    const { activeJobEntry, activeBreakEntry } = useMemo(() => {
        let jobEntry: TimeEntriesExpanded | null = null
        let breakEntry: TimeEntriesExpanded | null = null

        for (const row of rawEntries) {
            const entry = row.entries as TimeEntriesExpanded
            const expanded = {
                ...entry,
                expand: {
                    ...entry.expand,
                    job: row.job ?? entry.expand?.job,
                    location: row.location ?? entry.expand?.location,
                },
            } as TimeEntriesExpanded

            if (!breakEntry && expanded.entry_type === ('break' as string)) {
                breakEntry = expanded
            } else if (!jobEntry) {
                jobEntry = expanded
            }
            if (jobEntry && breakEntry) break
        }

        return { activeJobEntry: jobEntry, activeBreakEntry: breakEntry }
    }, [rawEntries])

    return {
        activeEntry: activeJobEntry,
        activeJobEntry,
        activeBreakEntry,
        isActive: Boolean(activeJobEntry),
        isOnBreak: Boolean(activeBreakEntry),
        isLoading,
    }
}
